import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchCompanySmartContractSettingsCacheKey,
  FetchCacheCompanySmartContractSettingsFilters,
  FetchCompanySmartContractSettingsSort,
  FetchCompanySmartContractSettingsPage,
  FetchCompanySmartContractSettingsLimit,
  FetchCompanySmartContractSettingsGqlQuery
} from '../../companySmartContractSettingsTypes';
import { FetchCacheCompanySmartContractSettingsQueryResponse } from '../../queries/fetchCacheCompanySmartContractSettings.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_COMPANY_SMART_CONTRACT_SETTING_FILTERS,
  INITIAL_COMPANY_SMART_CONTRACT_SETTING_LIMIT,
  INITIAL_COMPANY_SMART_CONTRACT_SETTING_PAGE,
  INITIAL_COMPANY_SMART_CONTRACT_SETTING_SORT
} from '../../companySmartContractSettingsConstants';

interface CompanySmartContractSettingsWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface CompanySmartContractSettingsWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface CompanySmartContractSettingsDefaultOptions {
  action?: string;
  cacheKey: FetchCompanySmartContractSettingsCacheKey;
  query: FetchCompanySmartContractSettingsGqlQuery;
  initialFilters?: FetchCacheCompanySmartContractSettingsFilters;
  initialSort?: FetchCompanySmartContractSettingsSort;
  initialPage?: FetchCompanySmartContractSettingsPage;
  initialLimit?: FetchCompanySmartContractSettingsLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type CompanySmartContractSettingsOptions =
  CompanySmartContractSettingsDefaultOptions &
    (
      | CompanySmartContractSettingsWithPrefetchItemOptions
      | CompanySmartContractSettingsWithoutPrefetchItemOptions
    );

const scope = 'companySmartContractSettings';

function useCachePaginatedCompanySmartContractSettings<
  FetchCompanySmartContractSettingsType = FetchCacheCompanySmartContractSettingsQueryResponse
>({
  action,
  cacheKey,
  initialFilters = INITIAL_COMPANY_SMART_CONTRACT_SETTING_FILTERS,
  initialSort = INITIAL_COMPANY_SMART_CONTRACT_SETTING_SORT,
  initialPage = INITIAL_COMPANY_SMART_CONTRACT_SETTING_PAGE,
  initialLimit = INITIAL_COMPANY_SMART_CONTRACT_SETTING_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: CompanySmartContractSettingsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchCompanySmartContractSettingsType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    companySmartContractSettingsData: data,
    companySmartContractSettings: items,
    companySmartContractSettingsError: itemsError,
    companySmartContractSettingsErrorMessage: itemsErrorMessage,
    companySmartContractSettingsTotalCount: itemsTotalCount,
    companySmartContractSettingsFetched: isFetched,
    companySmartContractSettingsLoading: isLoading,
    companySmartContractSettingsIsPlaceholderData: isPlaceholderData,
    companySmartContractSettingsFilters: currentFilters,
    companySmartContractSettingsSort: currentSort,
    companySmartContractSettingsPage: currentPage,
    companySmartContractSettingsLimit: currentLimit,
    updateCompanySmartContractSettingCache: updateItemCache,
    updateCompanySmartContractSettingsCache: updateItemsCache,
    filterCompanySmartContractSettings: filterItems,
    changeCompanySmartContractSettingsFilters: changeItemsFilters,
    clearCompanySmartContractSettingsFilters: clearItemsFilters,
    sortCompanySmartContractSettings: sortItems,
    paginateCompanySmartContractSettings: paginateItems,
    limitCompanySmartContractSettings: limitItems,
    prefetchCompanySmartContractSettings: prefetchItems,
    prefetchCompanySmartContractSetting: prefetchItem
  };
}

export default useCachePaginatedCompanySmartContractSettings;
